import { MAIL_REVIEW_MAX, type MailReviewSettings, type MailReviewView } from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { Button } from '../../ui/button';
import { ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Checkbox } from '../../ui/form';
import { Panel } from '../../ui/layout';

const KEY = ['admin', 'mail-review'];

/**
 * Режим проверки почты компании (УВ-13): пока портал настраивается, все письма портала по компании приходят
 * проверяющим с пометкой, кому предназначались; приглашения уходят адресату.
 */
export function MailReviewPanel() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: KEY, queryFn: () => api<MailReviewView>('/admin/mail-review') });
  const [form, setForm] = useState<MailReviewSettings | null>(null);
  useEffect(() => {
    if (q.data && !form) setForm(q.data.settings);
  }, [q.data, form]);
  const save = useMutation({
    mutationFn: (body: MailReviewSettings) =>
      api<MailReviewView>('/admin/mail-review', { method: 'PUT', body }),
    onSuccess: (x) => {
      qc.setQueryData(KEY, x);
      setForm(x.settings);
      toast(x.settings.enabled ? 'Режим проверки почты включён' : 'Режим проверки почты выключен');
    },
  });

  const toggle = (id: string) => {
    if (!form) return;
    const has = form.reviewerIds.includes(id);
    setForm({
      ...form,
      reviewerIds: has ? form.reviewerIds.filter((x) => x !== id) : [...form.reviewerIds, id],
    });
  };

  return (
    <Panel title="Режим проверки почты" meta="УВ-13; изменения — в «Истории изменений»">
      <div className="flex max-w-2xl flex-col gap-4 text-[14px]">
        <p className="text-ink-2">
          Пока портал настраивается, письма портала по компании (сводки, поручения, поддержка, контроль
          сроков, отчёты по расписанию) никому не уходят, кроме проверяющих: каждое письмо приходит им с
          пометкой, кому оно предназначалось. Приглашения в портал уходят адресату. Сообщения в Telegram режим
          не меняет.
        </p>
        {q.isError ? (
          <ErrorBox error={q.error} />
        ) : q.isPending || !form ? (
          <Spinner />
        ) : (
          <>
            <Checkbox
              className="min-h-11 items-center"
              label="Включить режим проверки"
              checked={form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
            />
            <fieldset className="flex flex-col gap-1">
              <legend className="mb-1 text-[13px] font-medium text-ink">
                Проверяющие (до {MAIL_REVIEW_MAX}): владелец и администраторы компании
              </legend>
              {!q.data.candidates.length ? (
                <p className="text-ink-2">Нет администраторов компании с адресом почты.</p>
              ) : (
                q.data.candidates.map((c) => (
                  <Checkbox
                    key={c.userId}
                    className="min-h-11 items-center"
                    label={c.fullName}
                    description={c.email}
                    checked={form.reviewerIds.includes(c.userId)}
                    disabled={
                      !form.reviewerIds.includes(c.userId) && form.reviewerIds.length >= MAIL_REVIEW_MAX
                    }
                    onChange={() => toggle(c.userId)}
                  />
                ))
              )}
            </fieldset>
            {form.enabled && !form.reviewerIds.length ? (
              <p className="text-danger">
                Выберите хотя бы одного проверяющего — иначе письма не уйдут никому.
              </p>
            ) : null}
            {!q.data.mailConfigured ? (
              <p className="text-danger">Почта портала не настроена — письма не отправляются.</p>
            ) : null}
            {q.data.isDemo ? <p className="text-ink-2">В демо-компании письма не отправляются.</p> : null}
            <ErrorBox error={save.error} />
            <div className="flex flex-wrap gap-2">
              <Button
                variant="primary"
                loading={save.isPending}
                disabled={
                  JSON.stringify(form) === JSON.stringify(q.data.settings) ||
                  (form.enabled && !form.reviewerIds.length)
                }
                onClick={() => save.mutate(form)}
              >
                Сохранить
              </Button>
            </div>
            {q.data.recent.length ? (
              <section className="border-t border-line pt-4">
                <h3 className="mb-2 text-[13px] font-medium text-ink">Последние письма на проверку</h3>
                <ul className="flex flex-col gap-1">
                  {q.data.recent.map((r) => (
                    <li key={`${r.at}-${r.subject}`} className="[overflow-wrap:anywhere] text-ink-2">
                      {dateTime(r.at)} — {r.subject} <span className="text-ink">→ {r.intendedName}</span>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </>
        )}
      </div>
    </Panel>
  );
}
